import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import { usePageHeader } from "../../context/LayoutContext";
import {
  VideoPreviewV2,
  type VideoPreviewHandle,
} from "../../components/video/VideoPreviewV2";
import { PlanApprovalCard } from "./components/PlanApprovalCard";
import { resolveCitationTarget } from "../../features/narrative-citations/resolveCitationTarget";
import type {
  Citation,
  CitationGroup,
  CitationSource,
} from "../../features/narrative-citations/types";
import { useNarrativeCitationsEnabled } from "../../hooks/useNarrativeCitationsEnabled";
import { RevisionCountdown } from "./components/RevisionCountdown";
import { usePlanReview } from "./hooks/usePlanReview";

const ReviewPlanPage: React.FC = () => {
  const {
    threadId,
    thread,
    reportMetadata,
    videoData,
    videoTitles,
    currentPlan,
    isLoading,
    isLoadingEvidence,
    isSubmitting,
    isAwaitingRevision,
    error,
    evidenceError,
    statusMessage,
    feedback,
    setFeedback,
    submitApproval,
    submitFeedback,
    handleReturnHome,
    refreshThread,
    entities,
    entitiesEdited,
    gaps,
    updateEntity,
    removeEntity,
    restoreEntity,
    addEntity,
    reorderEntities,
    resolveGap,
    revisionStartedAt,
    revisionTimeoutMs,
  } = usePlanReview();

  const theme = useTheme();

  const { enabled: citationsEnabled } = useNarrativeCitationsEnabled();

  // Seek the evidence player to a citation's grounding moment.
  const videoPreviewRef = React.useRef<VideoPreviewHandle>(null);
  const seekToCitation = React.useCallback(
    (citation: Citation) => {
      const videos = Array.isArray(videoData)
        ? videoData
        : videoData
          ? [videoData]
          : [];
      const video = videos.find((v) => v.id === citation.evidenceId);
      const paragraphs = (video?.transcript as { paragraphs?: unknown[] } | undefined)
        ?.paragraphs as Parameters<typeof resolveCitationTarget>[1];
      const target = resolveCitationTarget(citation, paragraphs ?? null);
      videoPreviewRef.current?.seekToEvidence(citation.evidenceId, target.seconds);
    },
    [videoData],
  );
  // Entity citation chip (HITL plan preview) → seek.
  const handleCiteSource = React.useCallback(
    (source: CitationSource) => seekToCitation(source),
    [seekToCitation],
  );
  // Inline Outline-briefing anchor → seek to the first cited source.
  const handleCiteMarkdown = React.useCallback(
    (group: CitationGroup) => {
      const citation = group.citations[0];
      if (citation) seekToCitation(citation);
    },
    [seekToCitation],
  );

  const reportLabel =
    reportMetadata?.reportDescription
    || (videoTitles?.length ? videoTitles.join(", ") : undefined)
    || "Approve or revise the outline.";

  usePageHeader({
    title: "Review Outline",
    subtitle: reportLabel,
    chip: thread?.status ? { label: thread.status } : undefined,
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <DescriptionOutlinedIcon color="warning" />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Outline Approval
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {threadId
                    ? `${reportLabel} — paused until you review the outline.`
                    : "No report was selected."}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => void refreshThread()}
                  variant="outlined"
                  disabled={isLoading}
                >
                  Refresh
                </Button>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleReturnHome}
                  variant="text"
                >
                  Home
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip label={`Status: ${thread?.status || "unknown"}`} size="small" />
              {reportMetadata && (
                <Chip
                  label={`Evidence: ${reportMetadata.evidenceIds.length}`}
                  size="small"
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        {statusMessage && <Alert severity="info">{statusMessage}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {evidenceError && <Alert severity="warning">{evidenceError}</Alert>}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            alignItems: "flex-start",
          }}
        >
          {/* Left panel: Video evidence (sticky on desktop) */}
          <Box
            sx={{
              width: { xs: "100%", md: "58.333%" },
              flexShrink: 0,
              position: { md: "sticky" },
              top: { md: `calc(64px + ${theme.spacing(3)})` },
              maxHeight: { md: `calc(100vh - 64px - ${theme.spacing(6)})` },
              overflowY: { md: "auto" },
            }}
          >
            <Card
              variant="outlined"
              sx={{
                overflow: "hidden",
                borderRadius: 3,
                background: (theme) =>
                  `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <VideoLibraryOutlinedIcon color="primary" />
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Evidence Verification
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Review the attached bodycam video before approving the outline.
                    </Typography>
                  </Box>

                  {isLoadingEvidence ? (
                    <Typography color="text.secondary">
                      Loading attached evidence...
                    </Typography>
                  ) : videoData ? (
                    <VideoPreviewV2
                      ref={videoPreviewRef}
                      videoData={videoData}
                      isMinimizable={false}
                    />
                  ) : (
                    <Alert severity="info">
                      No evidence is attached to this report.
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Right panel: Plan approval */}
          <Box sx={{ width: { xs: "100%", md: "41.667%" }, minWidth: 0, flexGrow: 1 }}>
            {currentPlan ? (
              <PlanApprovalCard
                plan={currentPlan}
                feedback={feedback}
                onFeedbackChange={setFeedback}
                onApprove={submitApproval}
                onRequestChanges={submitFeedback}
                isSubmitting={isSubmitting}
                isAwaitingRevision={isAwaitingRevision}
                entities={entities}
                entitiesEdited={entitiesEdited}
                gaps={gaps}
                onUpdateEntity={updateEntity}
                onRemoveEntity={removeEntity}
                onRestoreEntity={restoreEntity}
                onAddEntity={addEntity}
                onReorderEntities={reorderEntities}
                onResolveGap={resolveGap}
                onCiteSource={handleCiteSource}
                onCiteMarkdown={citationsEnabled ? handleCiteMarkdown : undefined}
              />
            ) : (
              <Card variant="outlined">
                <CardContent>
                  {isLoading ? (
                    <Typography color="text.secondary">
                      Loading outline state...
                    </Typography>
                  ) : (
                    <>
                      <Alert severity="info">
                        {isAwaitingRevision
                          ? "We are regenerating the outline based on your feedback. Please wait here or return Home if you wish to do something else."
                          : "There is no current outline waiting for approval. Refresh to check again."}
                      </Alert>
                      {isAwaitingRevision && revisionStartedAt && (
                        <RevisionCountdown
                          startedAt={revisionStartedAt}
                          timeoutMs={revisionTimeoutMs}
                        />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Stack>
    </Container>
  );
};

export default ReviewPlanPage;
